// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useMinimalClient } from 'platform/settings/hooks/useMinimalClient';
import {
    Box,
    Flex,
    Stack,
    Position,
    Heading,
    useToast,
    Icon,
    Loader
} from '@locus-taxy/neo-ui';
import { rankWith, uiTypeIs } from '@jsonforms/core';
import { useUpdateDomains } from 'platform/settings/hooks/useDomains';
import { SaveWithReasonButton } from 'platform/settings/form/renderers/components/SaveWithReasonButton';
import { ApiStatusEnum } from 'common/enums/API';
import useLayoutProps from '../util/useLayoutProps';
import MultiChipTextField from '../controls/MultiChipInputControl/MultiChipTextField';

type Props = {};

const DomainManagementLayoutRenderer = (props: Props): React.Node => {
    const minimalClient = useMinimalClient();
    const { uischema } = useLayoutProps(props);
    const { showToast } = useToast();
    const { t } = useTranslation('platform');

    const [value, setValue] = React.useState({
        domains: []
    });

    const updateDomains = useUpdateDomains();

    React.useEffect(() => {
        if (minimalClient.data?.domains) {
            setValue({
                domains: minimalClient.data.domains
            });
        }
    }, [minimalClient.data]);

    const onSave = (reason: string) => {
        const domainsToAdd = value.domains.filter(
            domain => !minimalClient.data?.domains.includes(domain)
        );
        const domainsToDelete = minimalClient.data?.domains.filter(
            domain => !value.domains.includes(domain)
        );
        const payload = {
            domainsToAdd: domainsToAdd.length > 0 ? domainsToAdd : undefined,
            domainsToDelete:
                domainsToDelete.length > 0 ? domainsToDelete : undefined,
            reason
        };

        updateDomains.mutate(payload, {
            onSuccess: () => {
                showToast({
                    title: 'Domains updated successfully.',
                    palette: 'success',
                    icon: (
                        <Icon
                            icon="check_circle"
                            size="xLarge"
                            color="green.500"
                        />
                    )
                });
            },
            onError: error => {
                showToast({
                    title: error.message,
                    palette: 'danger',
                    icon: (
                        <Icon
                            icon="cross_circle"
                            size="xLarge"
                            color="red.600"
                        />
                    )
                });
            }
        });
    };

    const onChange = domains => {
        setValue({
            domains
        });
    };

    if (minimalClient.status === ApiStatusEnum.Loading) {
        return <Loader />;
    }

    return (
        <Stack space="medium">
            <Heading size="large" color="grey.900">
                {t(uischema.label)}
            </Heading>
            <Box width="100%">
                <Stack space={uischema.options?.space || 'medium'}>
                    <Box>
                        <MultiChipTextField
                            label={t(
                                'settings.platform.fields.clientSettings.fields.domainManagement.fields.domains.label'
                            )}
                            value={value.domains}
                            onChange={onChange}
                            isMaraOnlyField
                        />
                    </Box>
                    <Position
                        position="absolute"
                        bottom="0"
                        right="0"
                        width="100%"
                    >
                        <Flex width="100%" p="4" flexDirection="row-reverse">
                            <SaveWithReasonButton
                                label={t('save')}
                                showReasonModal
                                onSave={onSave}
                                disabled={false}
                                isSaving={
                                    updateDomains.status ===
                                    ApiStatusEnum.Loading
                                }
                            />
                        </Flex>
                    </Position>
                </Stack>
            </Box>
        </Stack>
    );
};

export default DomainManagementLayoutRenderer;

export const domainManagementLayoutTester = rankWith(
    1,
    uiTypeIs('DomainManagementLayout')
);
